import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

// 支持的语言列表
export const supportedLanguages = {
  zh: '中文',
  en: 'English',
  ru: 'Русский'
};

// 语言选项（包含"跟随系统"）
// 注意：system 选项的显示名称需要通过 i18n 动态获取
export const supportedLanguageOptions = {
  system: 'system', // 这个值会在运行时通过 i18n 替换为对应语言的翻译
  zh: '中文',
  en: 'English',
  ru: 'Русский'
};

// 语言映射 - 将子语言区域映射到我们支持的语言
const languageMapping: Record<string, string> = {
  'en-US': 'en',
  'en-GB': 'en',
  'en-CA': 'en',
  'en-AU': 'en',
  'zh-CN': 'zh',
  'zh-TW': 'zh',
  'zh-HK': 'zh',
  'ru-RU': 'ru'
};

// 初始化 i18next
i18n
  // 加载翻译文件的后端
  .use(HttpBackend)
  // 语言检测插件
  .use(LanguageDetector)
  // 传递i18n实例给react-i18next
  .use(initReactI18next)
  // 初始化配置
  .init({
    // 默认语言
    fallbackLng: 'zh',

    // 是否在控制台打印调试信息
    debug: import.meta.env.DEV,

    // 命名空间配置
    ns: [
      'common',
      'v0/common', 'v0/tasks', 'v0/chat', 'v0/projects', 'v0/calendar', 'v0/auth', 'v0/me', 'v0/subscription', 'v0/settings', 'v0/notifications',
      'v1/layout', 'v1/chat', 'v1/gui', 'v1/navigation',
      'v3/common', 'v3/tasks', 'v3/chat', 'v3/projects', 'v3/calendar', 'v3/auth', 'v3/me', 'v3/focus', 'v3/clock', 'v3/ios', 'v3/settings', 'v3/desktop', 'v3/appOnboarding', 'v3/character'
    ],
    defaultNS: 'v0/common',

    // 翻译文件加载路径
    backend: {
      loadPath: '/locales/{{lng}}/translation/{{ns}}.json',
    },

    // 语言检测器配置
    detection: {
      // 缓存用户语言偏好
      caches: ['localStorage'],
      // 检测顺序：先查找URL参数，再查找localStorage，再查看cookie，最后检测浏览器设置
      order: ['querystring', 'localStorage', 'cookie', 'navigator'],
      // 本地存储的键名
      lookupLocalStorage: 'i18nextLng'
    },

    // 仅使用支持的语言
    supportedLngs: Object.keys(supportedLanguages),

    // 语言标准化 - 将子区域语言映射到通用语言
    load: 'languageOnly',

    // 插值配置
    interpolation: {
      // React已经安全地处理了输出，无需进行额外的HTML转义
      escapeValue: false
    },

    // 返回到顶层的键值
    returnObjects: false,
    returnEmptyString: false,

    // 对于嵌套的翻译键使用点分隔符
    keySeparator: '.',
  });

// 映射系统语言到支持的语言
export const mapSystemLanguageToSupported = (systemLang: string): keyof typeof supportedLanguages => {
  // 如果是支持的完整语言代码，直接返回
  if (systemLang in supportedLanguages) {
    return systemLang as keyof typeof supportedLanguages;
  }
  // 否则尝试映射到基础语言
  const mapped = languageMapping[systemLang] || systemLang.split('-')[0];
  return (mapped in supportedLanguages) ? mapped as keyof typeof supportedLanguages : 'zh';
};

// 计算有效语言（处理 system 选项）
export const calculateEffectiveLanguage = (userPreference: string): keyof typeof supportedLanguages => {
  if (userPreference === 'system') {
    const systemLanguage = navigator.language || 'zh';
    return mapSystemLanguageToSupported(systemLanguage);
  }
  return userPreference in supportedLanguages ? userPreference as keyof typeof supportedLanguages : 'zh';
};

// 手动切换语言的函数
export const changeLanguage = (lng: keyof typeof supportedLanguageOptions) => {
  const effectiveLanguage = calculateEffectiveLanguage(lng);
  return i18n.changeLanguage(effectiveLanguage);
};

// 获取当前语言（简化版本，不包含区域）
export const getCurrentLanguage = () => {
  const fullLang = i18n.language || 'zh';
  // 如果是支持的完整语言代码，直接返回
  if (fullLang in supportedLanguages) {
    return fullLang;
  }
  // 否则尝试映射到基础语言
  return languageMapping[fullLang] || fullLang.split('-')[0] || 'zh';
};

export default i18n;